import { useState } from "react";
import { Label } from "@/components/ui/label";

/**
 * FASE 17.8 — units multi-picker. Empty selection = "todas as unidades".
 * value: array of unit ids; onChange(nextArray).
 */
export default function UnitsPicker({
  value = [],
  onChange,
  units = [],
  label = "Unidades",
  description = "A quais unidades este item pertence.",
  allowAll = true,
  testPrefix = "units-picker",
}) {
  const [mode, setModeState] = useState(value.length > 0 || !allowAll ? "some" : "all");

  const setMode = (next) => {
    setModeState(next);
    if (next === "all") onChange([]);
  };

  const toggle = (uid) => {
    const cur = new Set(value || []);
    if (cur.has(uid)) cur.delete(uid);
    else cur.add(uid);
    onChange(Array.from(cur));
  };

  return (
    <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40" data-testid={testPrefix}>
      <Label className="font-body text-sm font-semibold text-stone-800">{label}</Label>
      <p className="text-[11px] text-stone-500 font-body mt-0.5">{description}</p>
      {allowAll && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {[
            { key: "all", label: "Todas as unidades", desc: "Sem restrição" },
            { key: "some", label: "Unidades específicas", desc: "Uma ou mais" },
          ].map((opt) => (
            <label
              key={opt.key}
              className={`flex flex-col items-start gap-0.5 rounded-md border px-2 py-1.5 cursor-pointer transition-colors ${
                mode === opt.key
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-stone-200 bg-white hover:border-stone-300"
              }`}
            >
              <div className="flex items-center gap-1.5">
                <input
                  type="radio"
                  checked={mode === opt.key}
                  onChange={() => setMode(opt.key)}
                  className="accent-emerald-700"
                  data-testid={`${testPrefix}-${opt.key}`}
                />
                <span className="text-sm font-semibold">{opt.label}</span>
              </div>
              <span className="text-[10px] text-stone-600 font-body pl-5">{opt.desc}</span>
            </label>
          ))}
        </div>
      )}

      {(mode === "some" || !allowAll) && (
        <div
          className="mt-3 border border-stone-200 rounded-md bg-white max-h-48 overflow-y-auto p-1.5 space-y-0.5"
          data-testid={`${testPrefix}-list`}
        >
          {units.length === 0 && (
            <div className="text-xs text-stone-500 font-body italic py-2 text-center">
              Nenhuma unidade cadastrada ainda.
            </div>
          )}
          {units.map((u) => {
            const checked = (value || []).includes(u.id);
            return (
              <label
                key={u.id}
                className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-stone-50 px-2 py-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(u.id)}
                  className="accent-emerald-700"
                  data-testid={`${testPrefix}-unit-${u.id}`}
                />
                <span className="flex-1 truncate">{u.name}</span>
                {u.city && <span className="text-[10px] text-stone-500">{u.city}</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
